import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Loader2, ArrowLeft, Clock, FileText, CheckCircle2, Circle, Map } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RoadmapPage() {
  const { roadmapId } = useParams();
  const { t } = useTranslation();
  const [roadmaps, setRoadmaps] = useState([]);
  const [selectedRoadmap, setSelectedRoadmap] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        if (roadmapId) {
          const res = await axios.get(`${API}/roadmaps/${roadmapId}`);
          setSelectedRoadmap(res.data);
        } else {
          const res = await axios.get(`${API}/roadmaps`);
          setRoadmaps(res.data);
        }
      } catch { /* ignore */ }
      setLoading(false);
    };
    load();
  }, [roadmapId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="roadmap-loading">
        <Loader2 className="h-8 w-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  // Roadmap list
  if (!roadmapId && !selectedRoadmap) {
    return (
      <main className="min-h-screen bg-background" data-testid="roadmaps-list">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-slate-900 font-heading mb-2">{t("pages.roadmapsTitle")}</h1>
            <p className="text-base text-slate-500">{t("pages.roadmapsSubtitle")}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {roadmaps.map((rm) => (
              <Link to={`/roadmaps/${rm.roadmap_id}`} key={rm.roadmap_id}>
                <Card className="border-slate-100 hover:border-teal-200 hover:shadow-md transition-all duration-300 h-full card-hover" data-testid={`roadmap-card-${rm.roadmap_id}`}>
                  <CardContent className="p-6">
                    <div className="h-12 w-12 rounded-xl bg-violet-50 flex items-center justify-center mb-4">
                      <Map className="h-6 w-6 text-violet-600" />
                    </div>
                    <Badge variant="outline" className="text-xs capitalize mb-2">{rm.category?.replace("_", " ")}</Badge>
                    <h3 className="text-base font-semibold text-slate-900 mb-2">{rm.title}</h3>
                    <p className="text-sm text-slate-500 line-clamp-2 mb-3">{rm.description}</p>
                    <div className="flex items-center gap-2 text-xs text-slate-400">
                      <Clock className="h-3.5 w-3.5" />
                      <span>{rm.estimated_timeline}</span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </main>
    );
  }

  // Roadmap detail
  const rm = selectedRoadmap;
  if (!rm) return null;

  return (
    <main className="min-h-screen bg-background" data-testid="roadmap-detail">
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <Link to="/roadmaps">
          <Button variant="ghost" className="mb-6 text-slate-600" data-testid="back-to-roadmaps">
            <ArrowLeft className="mr-2 h-4 w-4" /> {t("pages.backToRoadmaps")}
          </Button>
        </Link>

        <div className="mb-10">
          <Badge variant="outline" className="text-xs capitalize mb-3">{rm.category?.replace("_", " ")}</Badge>
          <h1 className="text-3xl font-bold text-slate-900 font-heading mb-2">{rm.title}</h1>
          <p className="text-base text-slate-600 mb-3">{rm.description}</p>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Clock className="h-4 w-4" />
            <span>{t("pages.estTimeline")} {rm.estimated_timeline}</span>
          </div>
        </div>

        {/* Timeline */}
        <div className="space-y-0">
          {rm.steps?.map((step, i) => (
            <div key={i} className="flex gap-6" data-testid={`roadmap-step-${step.step_number}`}>
              <div className="flex flex-col items-center">
                <div className="h-10 w-10 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0 border-2 border-teal-500">
                  <span className="text-sm font-bold text-teal-700">{step.step_number}</span>
                </div>
                {i < rm.steps.length - 1 && (
                  <div className="w-0.5 flex-1 bg-gradient-to-b from-teal-300 to-slate-200 my-2" />
                )}
              </div>
              <div className="pb-10 flex-1">
                <h3 className="text-lg font-semibold text-slate-900 mb-1">{step.title}</h3>
                <p className="text-sm text-slate-600 mb-3">{step.description}</p>
                <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
                  <Clock className="h-3.5 w-3.5" />
                  <span>{step.estimated_duration}</span>
                </div>
                {step.tips?.length > 0 && (
                  <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 mb-3">
                    <p className="text-xs font-medium text-teal-800 mb-1.5">{t("pages.tips")}</p>
                    <ul className="space-y-1">
                      {step.tips.map((tip, j) => (
                        <li key={j} className="text-xs text-teal-700 flex items-start gap-1.5">
                          <CheckCircle2 className="h-3 w-3 text-teal-500 mt-0.5 flex-shrink-0" /> {tip}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {step.required_forms?.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {step.required_forms.map((f) => (
                      <Badge key={f} variant="outline" className="text-xs">
                        <FileText className="h-3 w-3 mr-1" /> {f}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </main>
  );
}
